import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next, view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import getBatchValues from 'src/js/modules/element-settings/helpers/get-batch-values';
import AlignmentsControl from '../components/alignments-control';
import AmountControl from '../components/amount-control';
import WidthControl from '../components/width-control';
import OrderControl from '../components/order-control';
import ColorSchemeControl from '../components/color-scheme-control';
import CornersControl from '../components/corners-control';
import CornersSectionControl from '../components/corners-section-control';
import HorizontalGapControl from '../components/horizontal-gap-control';
import HorizontalSpacingControl from '../components/horizontal-spacing-control';
import SpacingSectionControl from '../components/spacing-section-control';
import ShadowControl from '../components/shadow-control';
import BorderControl from '../components/border-control';
import VerticalSpacingControl from '../components/vertical-spacing-control';
import VerticalGapControl from '../components/vertical-gap-control';
import useMigrateLegacyAttributes from '../attribute-migration/use-migrate-legacy-attributes';
import controlIds from '../constants/control-ids';
import previewGifs from '../constants/preview-gifs';
import { columnsOrderDirectionTargets } from '../constants/targets';
import getColumnsControlSettings from '../settings/columns-control-settings';
import createSubmitTraitsColumnAppearanceHandler from '../helpers/create-submit-traits-column-appearance-handler';
import generateColumnAppearanceInitialState from '../helpers/generate-column-appearance-initial-state';

const { Box, Divider, Stack } = designSystem;
const {
  Context: ElementSettingsContext,
  components: { Section, ActionRadioGroupControl, SelectControl },
  constants: { stages, targetAttributes },
  helpers: { createBatch },
  hooks: { useMountChunk },
} = elementSettings;

const EVENT_TRAITS_PREFIX = 'columns';

const DESKTOP_ONLY_CONTROL_IDS = [
  controlIds.COLUMNS_ALIGNMENTS,
  controlIds.COLOR_SCHEME,
  controlIds.HORIZONTAL_CONTENT_ALIGNMENT,
  controlIds.VERTICAL_CONTENT_ALIGNMENT,
  controlIds.COLUMN_HEIGHT,
  controlIds.NO_WRAP,
];

const createControlVisibilityChecker = (deviceMode) => (controlId) =>
  ![view.deviceModes.TABLET, view.deviceModes.MOBILE].includes(deviceMode?.active) ||
  !DESKTOP_ONLY_CONTROL_IDS.includes(controlId);

const ColumnsAppearanceSettingsContainer = ({ tabId, elementId, settingId }) => {
  const chunkId = tabId;
  const { resolvedState, callbacks, deviceMode, onUpdateState } =
    useContext(ElementSettingsContext);

  const isBreakpointEditingEnabled = Boolean(deviceMode.active);

  const [noWrapValue] = getBatchValues(resolvedState, {
    elementIds: [elementId],
    targets: [targetAttributes.NO_WRAP],
  }).flat();

  const controlSettings = useMemo(() => getColumnsControlSettings(elementId), [elementId]);
  const columnElementIds = controlSettings[controlIds.VERTICAL_SPACING].elementIds;

  const handleUpdateState = useCallback(
    (batch, options = {}) => onUpdateState(batch, { chunkId, ...options }),
    [chunkId, onUpdateState]
  );

  const handleChange = useCallback(
    ({
      controlId,
      elementIds,
      targets,
      value,
      skipApplyChanges = false,
      skipTracking = false,
      shouldScrollToTarget = true,
      batch: providedBatch,
    }) => {
      const batch = providedBatch || createBatch({ elementIds, targets, value });

      handleUpdateState(batch, { skipApplyChanges, shouldScrollToTarget });
      callbacks.onControlClick({ controlId, settingId, skipTracking });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleBatchChange = useCallback(
    ({ batch, controlId, skipTracking = false }) => {
      handleUpdateState(batch);
      callbacks.onControlClick({ controlId, settingId, skipTracking });
    },
    [callbacks, handleUpdateState, settingId]
  );

  const handleToggleChange = useCallback(
    ({
      controlId,
      elementIds,
      targets,
      value,
      isActive = value,
      skipTracking = false,
      shouldScrollToTarget = true,
    }) => {
      const batch = createBatch({ elementIds, targets, value });

      handleUpdateState(batch, { shouldScrollToTarget });
      callbacks.onToggleClick({ controlId, settingId, isActive, skipTracking });
    },
    [callbacks, handleUpdateState, settingId]
  );

  const handleToggleableSectionChange = useCallback(
    ({ batch, controlId, value, skipTracking = false, shouldScrollToTarget = true }) => {
      handleUpdateState(batch, { shouldScrollToTarget });
      callbacks.onToggleClick({ controlId, settingId, isActive: value, skipTracking });
    },
    [callbacks, handleUpdateState, settingId]
  );

  const handleColumnsAmountChange = useCallback(
    (batch, { shouldScrollToTarget = true } = {}) => {
      handleUpdateState(batch, { shouldScrollToTarget });
    },
    [handleUpdateState]
  );

  const handleColumnsAmountClick = useCallback(
    ({ controlId }) => {
      callbacks.onControlClick({ controlId, settingId });
    },
    [callbacks, settingId]
  );

  const traitsHandler = useMemo(
    () =>
      createSubmitTraitsColumnAppearanceHandler({
        prefix: EVENT_TRAITS_PREFIX,
        isBreakpointEditingEnabled,
      }),
    [isBreakpointEditingEnabled]
  );

  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    initialState: generateColumnAppearanceInitialState(controlSettings),
    stageHandlers: [
      {
        stage: stages.SUBMIT_TRAITS_GENERATION,
        handler: traitsHandler,
      },
    ],
  });

  useMigrateLegacyAttributes({
    enabled: isChunkMounted,
    columnElementIds,
    onUpdateState: handleUpdateState,
  });

  if (!isChunkMounted) return null;

  const isControlVisible = createControlVisibilityChecker(deviceMode);

  return (
    <>
      {isControlVisible(controlIds.COLUMNS_ALIGNMENTS) && (
        <>
          <AlignmentsControl elementId={elementId} onChange={handleChange} />
          <Divider appearance="light-grey" />
        </>
      )}
      {isControlVisible(controlIds.COLOR_SCHEME) && (
        <>
          <ColorSchemeControl
            {...controlSettings[controlIds.COLOR_SCHEME]}
            onChange={handleBatchChange}
          />
          <Divider appearance="light-grey" />
        </>
      )}
      {(isControlVisible(controlIds.HORIZONTAL_CONTENT_ALIGNMENT) ||
        isControlVisible(controlIds.VERTICAL_CONTENT_ALIGNMENT)) && (
        <>
          <Section title={i18next.t('Content alignment')}>
            {isControlVisible(controlIds.HORIZONTAL_CONTENT_ALIGNMENT) && (
              <ActionRadioGroupControl
                {...controlSettings[controlIds.HORIZONTAL_CONTENT_ALIGNMENT]}
                nested
                onChange={handleChange}
              />
            )}
            {isControlVisible(controlIds.VERTICAL_CONTENT_ALIGNMENT) && (
              <ActionRadioGroupControl
                {...controlSettings[controlIds.VERTICAL_CONTENT_ALIGNMENT]}
                nested
                onChange={handleChange}
              />
            )}
          </Section>
          <Divider appearance="light-grey" />
        </>
      )}
      <Section
        title={i18next.t('Layout')}
        popoverProps={{
          content: i18next.t('Select the number of columns per row'),
          imgSrc: previewGifs.COLUMNS_AMOUNT_GIF,
        }}
      >
        <Box paddingBottom="spacing-4-xs">
          <Stack gap="spacing-2-xs">
            {isControlVisible(controlIds.COLUMNS_AMOUNT) && (
              <AmountControl
                elementId={elementId}
                noWrapControl={
                  isControlVisible(controlIds.NO_WRAP) ? controlSettings[controlIds.NO_WRAP] : null
                }
                onChange={handleColumnsAmountChange}
                onClick={handleColumnsAmountClick}
                onNoWrapChange={handleToggleChange}
              />
            )}
            <Box paddingTop="spacing-4-xs">
              <WidthControl
                elementIds={controlSettings[controlIds.COLUMN_WIDTH].elementIds}
                targets={controlSettings[controlIds.COLUMN_WIDTH].targets}
                nested
                onChange={handleChange}
              />
            </Box>
            {isControlVisible(controlIds.COLUMN_HEIGHT) && (
              <SelectControl
                {...controlSettings[controlIds.COLUMN_HEIGHT]}
                nested
                onChange={handleChange}
              />
            )}
          </Stack>
        </Box>
        <Divider appearance="light-grey" />
        <Box paddingTop="spacing-4-xs">
          <OrderControl
            elementIds={controlSettings[controlIds.COLUMNS_ORDER].elementIds}
            targets={columnsOrderDirectionTargets}
            nested
            onChange={handleChange}
          />
        </Box>
      </Section>
      <Divider appearance="light-grey" />
      <SpacingSectionControl
        elementIds={{
          [controlIds.VERTICAL_SPACING]: controlSettings[controlIds.VERTICAL_SPACING].elementIds,
          [controlIds.HORIZONTAL_SPACING]:
            controlSettings[controlIds.HORIZONTAL_SPACING].elementIds,
          [controlIds.HORIZONTAL_GAP]: controlSettings[controlIds.HORIZONTAL_GAP].elementIds,
          [controlIds.VERTICAL_GAP]: controlSettings[controlIds.VERTICAL_GAP].elementIds,
        }}
        targets={{
          [controlIds.VERTICAL_SPACING]: controlSettings[controlIds.VERTICAL_SPACING].targets,
          [controlIds.HORIZONTAL_SPACING]: controlSettings[controlIds.HORIZONTAL_SPACING].targets,
          [controlIds.HORIZONTAL_GAP]: controlSettings[controlIds.HORIZONTAL_GAP].targets,
          [controlIds.VERTICAL_GAP]: controlSettings[controlIds.VERTICAL_GAP].targets,
        }}
        onChange={handleToggleableSectionChange}
      >
        <VerticalSpacingControl
          elementIds={controlSettings[controlIds.VERTICAL_SPACING].elementIds}
          targets={controlSettings[controlIds.VERTICAL_SPACING].targets}
          onChange={handleChange}
        />
        <HorizontalSpacingControl
          elementIds={controlSettings[controlIds.HORIZONTAL_SPACING].elementIds}
          targets={controlSettings[controlIds.HORIZONTAL_SPACING].targets}
          onChange={handleChange}
        />
        <Divider appearance="light-grey" />
        <HorizontalGapControl
          elementIds={controlSettings[controlIds.HORIZONTAL_GAP].elementIds}
          targets={controlSettings[controlIds.HORIZONTAL_GAP].targets}
          onChange={handleChange}
        />
        {!noWrapValue && (
          <VerticalGapControl
            elementIds={controlSettings[controlIds.VERTICAL_GAP].elementIds}
            targets={controlSettings[controlIds.VERTICAL_GAP].targets}
            onChange={handleChange}
          />
        )}
      </SpacingSectionControl>
      <Divider appearance="light-grey" />
      <CornersSectionControl
        elementIds={controlSettings[controlIds.CORNERS].elementIds}
        targets={controlSettings[controlIds.CORNERS].targets}
        onChange={handleToggleableSectionChange}
      >
        <CornersControl
          elementIds={controlSettings[controlIds.CORNERS].elementIds}
          targets={controlSettings[controlIds.CORNERS].targets}
          onChange={handleChange}
        />
      </CornersSectionControl>
      <Divider appearance="light-grey" />
      {isControlVisible(controlIds.BORDER) && (
        <>
          <BorderControl
            elementIds={controlSettings[controlIds.BORDER].elementIds}
            targets={controlSettings[controlIds.BORDER].targets}
            onChange={handleToggleableSectionChange}
          />
          <Divider appearance="light-grey" />
        </>
      )}
      {isControlVisible(controlIds.SHADOW) && (
        <ShadowControl
          elementIds={controlSettings[controlIds.SHADOW].elementIds}
          targets={controlSettings[controlIds.SHADOW].targets}
          onChange={handleToggleableSectionChange}
        />
      )}
    </>
  );
};

ColumnsAppearanceSettingsContainer.propTypes = {
  tabId: PropTypes.string.isRequired,
  elementId: PropTypes.string.isRequired,
  settingId: PropTypes.string.isRequired,
};

export default ColumnsAppearanceSettingsContainer;
